import { useCallback, useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import type { Bootstrap } from "../shared/types";
import { api } from "./lib/api";
import Sidebar from "./components/Sidebar";
import MobileHeader from "./components/MobileHeader";
import Login from "./pages/Login";
import MyPosition from "./pages/MyPosition";
import ThisMonth from "./pages/ThisMonth";
import Recurring from "./pages/Recurring";
import Spending from "./pages/Spending";
import PaymentPlans from "./pages/PaymentPlans";
import Savings from "./pages/Savings";
import Budget from "./pages/Budget";
import Breakdown from "./pages/Breakdown";
import PlanAhead from "./pages/PlanAhead";
import Activity from "./pages/Activity";
import Estimator from "./pages/Estimator";
import Settings from "./pages/Settings";

export default function App() {
  // null = still checking; otherwise whether a session exists + whether the
  // app still needs its first account created.
  const [auth, setAuth] = useState<{ authed: boolean; needsSetup: boolean } | null>(null);
  const authed = auth?.authed ?? false;
  const [data, setData] = useState<Bootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null until the first load resolves the cycle that contains today.
  const [period, setPeriod] = useState<string | null>(null);
  // Live connection state, for the offline banner.
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));

  const reload = useCallback(async () => {
    try {
      const d = await api.bootstrap(period ?? undefined);
      setData(d);
      if (period == null) setPeriod(d.summary.period);
      setError(null);
    } catch (e) {
      const message = (e as Error).message;
      // Session expired or lost mid-session — drop back to the login screen.
      if (message === "Not authenticated") {
        setAuth({ authed: false, needsSetup: false });
        return;
      }
      setError(message);
    }
  }, [period]);

  // Track connectivity so we can warn before a save silently fails.
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // On load, ask the server: is a session active, and does setup still need doing?
  useEffect(() => {
    api
      .authStatus()
      .then((s) => setAuth({ authed: s.authed, needsSetup: s.needsSetup }))
      .catch(() => setAuth({ authed: false, needsSetup: false }));
  }, []);

  // Once authenticated, load the budget.
  useEffect(() => {
    if (authed) reload();
  }, [authed, reload]);

  async function logout() {
    try {
      await api.logout();
    } finally {
      setData(null);
      setPeriod(null);
      setError(null);
      setAuth({ authed: false, needsSetup: false });
    }
  }

  const offlineBanner = !online ? (
    <div className="offline-banner" role="status">
      <span className="offline-banner__dot" aria-hidden="true" />
      You’re offline — changes won’t be saved until you reconnect.
    </div>
  ) : null;

  if (auth === null) return <div className="state">Loading…</div>;
  if (!auth.authed) {
    return (
      <>
        {offlineBanner}
        <Login
          needsSetup={auth.needsSetup}
          onSuccess={() => setAuth({ authed: true, needsSetup: false })}
        />
      </>
    );
  }

  return (
    <div className="app">
      <Sidebar household={data?.household} me={data?.actingUser} onLogout={logout} />
      <main className="app__main">
        {offlineBanner}
        <MobileHeader household={data?.household} me={data?.actingUser} onLogout={logout} />
        {error ? (
          <div className="state state--error" role="alert">
            Couldn’t load your budget: {error}
          </div>
        ) : (
          <Routes>
            <Route path="/me" element={<MyPosition />} />
            <Route path="/" element={<ThisMonth data={data} reload={reload} period={period ?? data?.summary.period ?? ""} setPeriod={setPeriod} />} />
            <Route path="/recurring" element={<Recurring data={data} reload={reload} />} />
            <Route path="/spending" element={<Spending data={data} reload={reload} />} />
            <Route path="/plans" element={<PaymentPlans data={data} reload={reload} />} />
            <Route path="/savings" element={<Savings data={data} />} />
            <Route path="/budget" element={<Budget data={data} reload={reload} />} />
            <Route path="/breakdown" element={<Breakdown data={data} />} />
            <Route path="/plan-ahead" element={<PlanAhead data={data} />} />
            <Route path="/activity" element={<Activity data={data} />} />
            <Route path="/estimator" element={<Estimator />} />
            <Route path="/settings" element={<Settings data={data} reload={reload} />} />
            <Route path="*" element={<div className="page"><h1 className="page__title">Not found</h1></div>} />
          </Routes>
        )}
      </main>
    </div>
  );
}
